import { useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  PERSON_CUSTOM_FIELD_TYPES,
  type CustomFieldDefinitionResponse,
  type PersonCustomFieldType,
} from '@tabita/contracts';
import { useAuth } from '@/auth/auth-context';
import { useI18n } from '@/i18n/i18n-provider';
import { peopleApi } from '@/services/people.api';
import { ApiError } from '@/services/api-client';
import { getShowArchivedRecordsPreference } from '@/services/record-visibility-preferences';
import { Alert, Button, EmptyState, Field, Spinner } from '@/components/ui';

const QUERY_KEY = ['people', 'customFieldDefinitions'];
const OPTION_TYPES: readonly PersonCustomFieldType[] = ['SELECT'];

function optionsFromText(text: string): string[] {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

function errorMessage(error: unknown, t: ReturnType<typeof useI18n>['t']): string {
  return error instanceof ApiError
    ? t(`errors.${error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
    : t('errors.INTERNAL_ERROR');
}

/**
 * Tenant administration of the `people.custom_fields` entitlement — the
 * fields an administrator adds to every person's Info tab.
 *
 * A tenant page, not a TABITA-operator one: gated on `people.customField.*`,
 * reached from Administration rather than Platform, the same boundary
 * `AdministrationPage` itself already draws for member/role management.
 */
export function CustomFieldsPage(): ReactNode {
  const { t } = useI18n();
  const { can } = useAuth();
  const queryClient = useQueryClient();
  // Controlled exclusively by My Settings — no per-page override, so an
  // archived record is never one accidental checkbox away from view.
  const showArchived = getShowArchivedRecordsPreference();

  const definitions = useQuery({
    queryKey: [...QUERY_KEY, showArchived],
    queryFn: () => peopleApi.listCustomFieldDefinitions(showArchived),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: QUERY_KEY, exact: false });

  const list = definitions.data ?? [];
  const canCreate = can('people.customField.create');

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">{t('people.customFields.definitionsTitle')}</h1>
          <p className="page-count">{t('people.customFields.subtitle')}</p>
        </div>
      </header>

      {canCreate && <CreateCustomFieldForm onCreated={() => void invalidate()} />}

      {definitions.isPending && <Spinner label={t('common.states.loading')} />}
      {definitions.isSuccess && list.length === 0 && (
        <EmptyState title={t('people.customFields.empty')} />
      )}

      {list.length > 0 && (
        <div className="table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{t('people.customFields.label')}</th>
                <th scope="col">{t('people.customFields.typeLabel')}</th>
                <th scope="col">{t('people.customFields.required')}</th>
                <th scope="col" />
              </tr>
            </thead>
            <tbody>
              {list.map((definition) => (
                <CustomFieldRow
                  key={definition.id}
                  definition={definition}
                  onChanged={() => void invalidate()}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CreateCustomFieldForm({ onCreated }: { readonly onCreated: () => void }) {
  const { t } = useI18n();
  const [label, setLabel] = useState('');
  const [type, setType] = useState<PersonCustomFieldType>('TEXT');
  const [required, setRequired] = useState(false);
  const [optionsText, setOptionsText] = useState('');
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      peopleApi.createCustomFieldDefinition({
        label: label.trim(),
        type,
        required,
        options: OPTION_TYPES.includes(type) ? optionsFromText(optionsText) : undefined,
      }),
    onSuccess: () => {
      setLabel('');
      setType('TEXT');
      setRequired(false);
      setOptionsText('');
      setError(null);
      onCreated();
    },
    onError: (err: unknown) => setError(errorMessage(err, t)),
  });

  return (
    <form
      className="filters"
      onSubmit={(event: FormEvent) => {
        event.preventDefault();
        if (label.trim()) create.mutate();
      }}
    >
      {error && <Alert tone="danger">{error}</Alert>}

      <Field
        label={t('people.customFields.label')}
        name="new-custom-field-label"
        value={label}
        onChange={(event) => setLabel(event.target.value)}
        required
      />

      <div className="field">
        <label className="field-label" htmlFor="new-custom-field-type">
          {t('people.customFields.typeLabel')}
        </label>
        <select
          id="new-custom-field-type"
          className="field-input"
          value={type}
          onChange={(event) => setType(event.target.value as PersonCustomFieldType)}
        >
          {PERSON_CUSTOM_FIELD_TYPES.map((value) => (
            <option key={value} value={value}>
              {t(`people.customFields.type.${value}`)}
            </option>
          ))}
        </select>
      </div>

      {OPTION_TYPES.includes(type) && (
        <div className="field">
          <label className="field-label" htmlFor="new-custom-field-options">
            {t('people.customFields.options')}
          </label>
          <textarea
            id="new-custom-field-options"
            className="field-input"
            value={optionsText}
            onChange={(event) => setOptionsText(event.target.value)}
            placeholder={t('people.customFields.optionsHint')}
          />
        </div>
      )}

      <label className="checkbox">
        <input
          type="checkbox"
          checked={required}
          onChange={(event) => setRequired(event.target.checked)}
        />
        {t('people.customFields.required')}
      </label>

      <Button type="submit" variant="primary" loading={create.isPending} disabled={!label.trim()}>
        {t('people.customFields.addField')}
      </Button>
    </form>
  );
}

function CustomFieldRow({
  definition,
  onChanged,
}: {
  readonly definition: CustomFieldDefinitionResponse;
  readonly onChanged: () => void;
}) {
  const { t } = useI18n();
  const { can } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [label, setLabel] = useState(definition.label);
  const [required, setRequired] = useState(definition.required);
  const [optionsText, setOptionsText] = useState((definition.options ?? []).join('\n'));
  const [error, setError] = useState<string | null>(null);

  const update = useMutation({
    mutationFn: () =>
      peopleApi.updateCustomFieldDefinition(definition.id, {
        label: label.trim(),
        required,
        options: OPTION_TYPES.includes(definition.type) ? optionsFromText(optionsText) : undefined,
      }),
    onSuccess: () => {
      setError(null);
      setIsEditing(false);
      onChanged();
    },
    onError: (err: unknown) => setError(errorMessage(err, t)),
  });

  const archive = useMutation({
    mutationFn: () => peopleApi.archiveCustomFieldDefinition(definition.id),
    onSuccess: onChanged,
    onError: (err: unknown) => setError(errorMessage(err, t)),
  });

  const restore = useMutation({
    mutationFn: () => peopleApi.restoreCustomFieldDefinition(definition.id),
    onSuccess: onChanged,
    onError: (err: unknown) => setError(errorMessage(err, t)),
  });

  const canUpdate = can('people.customField.update');
  const isArchived = definition.archivedAt !== null;

  if (isEditing) {
    return (
      <tr>
        <td colSpan={4}>
          {error && <Alert tone="danger">{error}</Alert>}
          <form
            className="filters"
            onSubmit={(event: FormEvent) => {
              event.preventDefault();
              update.mutate();
            }}
          >
            <Field
              label={t('people.customFields.label')}
              name={`edit-label-${definition.id}`}
              value={label}
              onChange={(event) => setLabel(event.target.value)}
              required
            />
            {OPTION_TYPES.includes(definition.type) && (
              <div className="field">
                <label className="field-label" htmlFor={`edit-options-${definition.id}`}>
                  {t('people.customFields.options')}
                </label>
                <textarea
                  id={`edit-options-${definition.id}`}
                  className="field-input"
                  value={optionsText}
                  onChange={(event) => setOptionsText(event.target.value)}
                />
              </div>
            )}
            <label className="checkbox">
              <input
                type="checkbox"
                checked={required}
                onChange={(event) => setRequired(event.target.checked)}
              />
              {t('people.customFields.required')}
            </label>
            <Button type="submit" variant="primary" loading={update.isPending}>
              {t('common.actions.save')}
            </Button>
            <Button type="button" variant="ghost" onClick={() => setIsEditing(false)}>
              {t('common.actions.cancel')}
            </Button>
          </form>
        </td>
      </tr>
    );
  }

  return (
    <tr>
      <td>{definition.label}</td>
      <td>{t(`people.customFields.type.${definition.type}`)}</td>
      <td>{t(definition.required ? 'common.labels.required' : 'common.labels.optional')}</td>
      <td>
        {error && <Alert tone="danger">{error}</Alert>}
        {canUpdate && !isArchived && (
          <Button variant="ghost" onClick={() => setIsEditing(true)}>
            {t('common.actions.edit')}
          </Button>
        )}
        {canUpdate && !isArchived && (
          <Button variant="ghost" loading={archive.isPending} onClick={() => archive.mutate()}>
            {t('common.actions.archive')}
          </Button>
        )}
        {canUpdate && isArchived && (
          <Button variant="ghost" loading={restore.isPending} onClick={() => restore.mutate()}>
            {t('common.actions.restore')}
          </Button>
        )}
      </td>
    </tr>
  );
}
